import {
    GetMeetingsQuery,
    GetMeetingsQueryVariables,
    MeetingFragmentFragment,
    UpdateMeeting as UpdateMeetingInput
} from 'generated/graphql'
import { useEventSlug, useQuery } from 'hooks'
import React, { useEffect, useMemo, useState } from 'react'
import { useDispatch } from 'react-redux'
import { useLocation, useParams } from 'react-router'
import { Formik } from 'formik'
import FormTabs from 'features/forms/FormTabs'
import { withCapture, _ } from 'utils'
import { getChangedValues } from 'features/forms/utils'
import { getNodes } from 'graphql/utils'
import { showErrorNotification, showNotification } from 'features/notifications/notificationsSlice'
import FullPageLoading from 'components/FullPageLoading'
import { meetingValidation } from './CreateMeeting'
import { meetingsQuery } from '../graphql/query'
import Settings from './tabs/Settings'
import Participants from './tabs/Participants'
import { updateMeeting } from '../graphql/mutation'

const transformDataForForm = (data: MeetingFragmentFragment): UpdateMeetingInput & { isOpen?: boolean } => ({
    ...data,
    startsAt: new Date(data.startsAt),
    endsAt: new Date(data.endsAt),
    programItemId: data?.programItem?.id as unknown as string,
})

const UpdateMeeting = () => {
    const eventSlug = useEventSlug()
    const location = useLocation<{ spaceId: number }>()
    const { meetingId } = useParams<{ meetingId: string }>()
    const spaceId = location.state?.spaceId
    const [initialValues, setInitialValues] = useState<UpdateMeetingInput>({})
    const [isUploading, setIsUploading] = useState(false)
    const dispatch = useDispatch()
    const [totalParticipants, setTotalParticipants] = useState(0)
    const { data, requestStatus } = useQuery<
        GetMeetingsQuery, GetMeetingsQueryVariables
    >(meetingsQuery, {
        eventId: eventSlug,
        first: 1,
        spaceFilters: { id: spaceId },
        meetingFilters: { id: +meetingId }
    })
    const meetingSpace = getNodes(data?.event?.meetingSpaces)[0]
    const meetingItem = getNodes(meetingSpace?.meetings)[0]

    useEffect(() => {
        if (meetingItem) {
            setInitialValues(transformDataForForm(meetingItem))
        }
    }, [meetingItem])

    const ParticipantsMemo = useMemo(() => () => (
        <Participants
            spaceId={spaceId}
            meetingId={+meetingId}
            setTotalParticipants={setTotalParticipants}
        />
    ), [meetingId, spaceId])

    if (requestStatus === 'loading') {
        return <FullPageLoading />
    }

    const handleSubmit = withCapture(async (
        values: UpdateMeetingInput & { isOpen: boolean }, { setFieldError, resetForm }
    ) => {
        if (values.capacity < totalParticipants) {
            setFieldError(
                'capacity',
                _('meeting.form.capacity_must_not_be_less_than_participants', {
                    value: totalParticipants
                })
            )
            return
        }

        setIsUploading(true)
        const submitValues = getChangedValues(initialValues, values)
        const response = await updateMeeting(meetingId, { ...submitValues })
        setIsUploading(false)

        if (!response.data || response.errors.length) {
            dispatch(showErrorNotification(response.errors))
            return
        }

        resetForm({ values })
        setInitialValues(values)
        dispatch(showNotification({
            type: 'success',
            body: _('form.notification.create_success'),
        }))
    })

    return (
        <Formik
            initialValues={initialValues}
            validationSchema={meetingValidation}
            onSubmit={handleSubmit}
            enableReinitialize
        >
            <>
                <FormTabs
                    tabs={[
                        { id: 'settings', name: _('meeting.tabs.settings'), Component: Settings },
                        {
                            id: 'participants',
                            name: _('meeting.tabs.participants').concat(` (${totalParticipants})`),
                            Component: ParticipantsMemo,
                        },
                    ]}
                />
                {isUploading && <FullPageLoading isAbsolute />}
            </>
        </Formik>
    )
}

export default UpdateMeeting
